import { UserWithMediaAndGroups } from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import {
  Box,
  BoxProps,
  Button,
  Flex,
  Text,
  FieldRoot,
  FieldLabel,
  FieldErrorText,
} from "@chakra-ui/react";
import { MouseEventHandler, useCallback, useMemo, useState } from "react";
import { apiReq } from "../../lib/apiReq";
import { GenderSelector } from "../GenderSelector/GenderSelector";
import { DateField } from "../DateField/DateField";
import dayjs from "dayjs";
import { store } from "../../lib/store/store";
import { ActionType } from "../../lib/store/storeActions";

interface DetailsScreenProps extends BoxProps {
  onNext: () => void;
  onClose?: () => void;
}

interface DetailsErrors {
  gender?: string;
  birthDate?: string;
}

export const DetailsScreen = ({
  onNext,
  onClose,
  ...props
}: DetailsScreenProps) => {
  const [detailsErrors, setDetailsErrors] = useState<DetailsErrors>({});
  const [gender, setGender] = useState<string | undefined>();
  const [birthDate, setBirthDate] = useState<string>("");

  const [detailsRequest, detailsRequestState] =
    useReq<UserWithMediaAndGroups>(apiReq);
  const date = useMemo(() => new Date(birthDate), [birthDate]);
  const validDate = !isNaN(date.getTime()) && date.getFullYear() !== 0;
  const age = useMemo(
    () => (validDate ? dayjs().diff(birthDate, "year") : 0),
    [birthDate, validDate]
  );
  const save = useCallback<MouseEventHandler>(async () => {
    const errors: DetailsErrors = {};
    if (!gender) {
      errors.gender = "Please select your gender.";
    }
    if (!birthDate) {
      errors.birthDate = "Please set your birthday.";
    } else if (!validDate) {
      errors.birthDate = "The birthday you set isn't valid.";
    } else if (age < 18) {
      errors.birthDate = "You must be 18 years or older to use Green Flag.";
    }
    setDetailsErrors(errors);
    if (!Object.keys(errors).length) {
      detailsRequestState.clearError();
      const response = await detailsRequest.put("/user/attributes", {
        birth_date: birthDate,
        gender,
      });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
        onNext();
      }
    }
  }, [
    age,
    birthDate,
    detailsRequest,
    detailsRequestState,
    gender,
    onNext,
    validDate,
  ]);
  return (
    <Box flex={1} px="0.25rem" {...props}>
      {!!detailsRequestState.error && (
        <Box
          mb="1rem"
          p="3"
          bg="red.50"
          borderRadius="md"
          borderWidth="1px"
          borderColor="red.500"
        >
          <Text color="red.700" fontWeight="700">
            There was a problem saving
          </Text>
        </Box>
      )}
      <Box mb="1.5rem">
        <DateField
          value={birthDate}
          onChange={setBirthDate}
          label="When is your birthday?"
          validationMessage={detailsErrors.birthDate}
        />
      </Box>
      <FieldRoot invalid={!!detailsErrors.gender} mt="1.5rem">
        <FieldLabel>What's your gender?</FieldLabel>
        <GenderSelector value={gender} onChange={setGender} />
        {detailsErrors.gender && (
          <FieldErrorText>{detailsErrors.gender}</FieldErrorText>
        )}
      </FieldRoot>

      <Flex
        py="2rem"
        direction={{ base: "column-reverse", sm: "row" }}
        gap="0.65rem"
      >
        {!!onClose && (
          <Button
            variant="ghost"
            flex={1}
            onClick={onClose}
            type="button"
            disabled={detailsRequestState.loading}
          >
            Cancel
          </Button>
        )}
        <Button
          variant="solid"
          colorPalette="greenDark"
          flex={2}
          disabled={detailsRequestState.loading}
          onClick={save}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};
